import { ChangeEvent, PureComponent } from 'react';

import { injectIntl, FormattedMessage, InjectedIntl } from 'react-intl';

import { Checkbox } from '@atlaskit/checkbox';
import Flag from '@atlaskit/flag';
import { Field } from '@atlaskit/form';
import Error from '@atlaskit/icon/glyph/error';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';

import Modal from 'src/components/modal-dialog-actions';
import { commonMessages } from 'src/i18n';
import {
  CommitAction,
  CommitResults,
  Ref,
} from 'src/sections/repository/types';

import { commitChange, CommitPayload } from '../sections/source/utils/commit';
import urls from '../urls';

import messages from './commit-modal-dialog.i18n';
import * as styles from './commit-modal-dialog.style';

type Props = {
  action: CommitAction;
  branchName?: string;
  // The path of the existing file, prior to the pending commit being applied
  currentFilePath: string;
  currentRef?: Ref;
  // An optional initial value for the updated file path (applicable for a "commit" or "rename")
  defaultNewFilePath?: string;
  fileContents?: string;
  intl: InjectedIntl;
  onClose: () => void;
  onSuccess: (results: CommitResults) => void;
  parentCommitHash?: string;
  repositoryFullSlug: string;
  requirePullRequest: boolean;
};

type State = {
  commitMessage: string;
  errorMessage: string | null | undefined;
  isCreatePullRequest: boolean;
  isRequesting: boolean;
  // The current value for the updated file path (the "rename" dialog has a user-editable input field)
  newFilePath: string;
  pullRequestBranchName: string;
};

class CommitModalDialog extends PureComponent<Props, State> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    onClose: () => {},
    onSuccess: () => {},
  };

  state = {
    commitMessage: `${this.props.currentFilePath} edited online with Bitbucket`,
    errorMessage: null,
    isCreatePullRequest: this.props.requirePullRequest,
    isRequesting: false,
    newFilePath: this.props.defaultNewFilePath || this.props.currentFilePath,
    pullRequestBranchName: '',
  };

  pullRequestClicked = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      isCreatePullRequest: event.target.checked,
    });
  };

  onChangeCommitMessage = (event: ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      commitMessage: event.currentTarget.value,
    });
  };

  onChangeNewFilePath = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.currentTarget) {
      return;
    }
    this.setState({
      newFilePath: event.currentTarget.value,
    });
  };

  onChangePullRequestBranchName = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.currentTarget) {
      return;
    }
    this.setState({
      pullRequestBranchName: event.currentTarget.value,
    });
  };

  onCommit = () => {
    const { action, branchName, intl, parentCommitHash, repositoryFullSlug } =
      this.props;

    const {
      commitMessage,
      isCreatePullRequest,
      newFilePath,
      pullRequestBranchName,
    } = this.state;

    const commitBranch = isCreatePullRequest
      ? pullRequestBranchName
      : branchName;

    if (!commitBranch) {
      this.setState({
        errorMessage: intl.formatMessage(messages.defaultBranchError),
      });
      return;
    }

    this.setState({ isRequesting: true });

    const commitPayload: CommitPayload = {
      branch: commitBranch,
      message: commitMessage,
      files: this.getFilesForAction(),
      parents: [parentCommitHash],
      repository: {
        full_name: repositoryFullSlug,
      },
      transient: false,
    };

    commitChange(repositoryFullSlug, commitPayload).then(
      commit => {
        const [owner, slug] = repositoryFullSlug.split('/');
        const pullRequestCreateUrl = isCreatePullRequest
          ? urls.ui.createPullRequest(owner, slug, commitBranch)
          : null;

        this.props.onSuccess({
          commitAction: action,
          commitHash: commit.hash,
          commitUrl: commit.links.html.href,
          filePath: newFilePath,
          pullRequestCreateUrl,
        });
      },
      error => {
        this.setState({ errorMessage: error.message, isRequesting: false });
      }
    );
  };

  getFilesForAction = () => {
    const { action, currentFilePath, fileContents } = this.props;
    const { newFilePath } = this.state;

    if (action === 'commit') {
      if (currentFilePath !== newFilePath) {
        return [
          { path: currentFilePath, content: null },
          { path: newFilePath, content: fileContents },
        ];
      }

      return [{ path: currentFilePath, content: fileContents }];
    }

    if (action === 'delete') {
      // delete is handled by just nulling the content of the file
      return [{ path: currentFilePath, content: null }];
    }

    if (action === 'rename') {
      // rename works by nulling the old path content and adding the
      // content at the new path
      return [
        { path: currentFilePath, content: null },
        { path: newFilePath, content: fileContents },
      ];
    }

    return [];
  };

  renderHeading() {
    const { action } = this.props;
    if (action === 'commit') {
      return <FormattedMessage {...messages.commitFileHeading} />;
    }
    if (action === 'delete') {
      return <FormattedMessage {...messages.deleteFileHeading} />;
    }
    if (action === 'rename') {
      return <FormattedMessage {...messages.renameFileHeading} />;
    }
    return null;
  }

  render() {
    const { action, currentFilePath, intl, onClose, requirePullRequest } =
      this.props;

    const {
      commitMessage,
      errorMessage,
      isCreatePullRequest,
      isRequesting,
      newFilePath,
      pullRequestBranchName,
    } = this.state;

    const actions = [
      { text: intl.formatMessage(commonMessages.cancel), onClick: onClose },
      {
        isDisabled:
          isRequesting ||
          !commitMessage ||
          (action === 'rename' && !newFilePath) ||
          (isCreatePullRequest && !pullRequestBranchName),
        isLoading: isRequesting,
        onClick: this.onCommit,
        text: isCreatePullRequest
          ? intl.formatMessage(messages.createPullRequestButton)
          : intl.formatMessage(commonMessages.commit),
      },
    ];

    const pullRequestCheckbox = (
      <Checkbox
        defaultChecked={requirePullRequest}
        isDisabled={requirePullRequest}
        onChange={this.pullRequestClicked}
        label={intl.formatMessage(messages.createPullRequestLabel)}
        name="use_pull_request"
        value="1"
      />
    );

    return (
      <Modal
        onClose={onClose}
        actions={actions}
        appearance={action === 'delete' ? 'danger' : undefined}
        heading={this.renderHeading()}
      >
        {errorMessage !== null && (
          <styles.RemovedDropShadow>
            <Flag
              id="commit-modal-dialog-error"
              key="commit-modal-dialog-error"
              title={intl.formatMessage(commonMessages.errorHeading)}
              description={errorMessage}
              appearance="normal"
              icon={<Error label="" />}
            />
          </styles.RemovedDropShadow>
        )}
        {action === 'delete' && (
          <FormattedMessage
            {...messages.deleteFileWarning}
            values={{ path: <code>{currentFilePath}</code> }}
          />
        )}
        {action === 'rename' && (
          <Field
            name="src-commit-modal-new-path-input"
            label={intl.formatMessage(messages.renameNewPathLabel)}
            isRequired
          >
            {({ fieldProps }: any) => (
              <TextField
                {...fieldProps}
                value={newFilePath}
                onChange={this.onChangeNewFilePath}
              />
            )}
          </Field>
        )}
        <styles.TextInputContainer>
          <Field
            name="src-commit-modal-commit-message-input"
            isRequired
            label={intl.formatMessage(messages.commitMessageLabel)}
          >
            {({ fieldProps }: any) => (
              <TextArea
                {...fieldProps}
                minimumRows={3}
                onChange={this.onChangeCommitMessage}
                value={commitMessage}
              />
            )}
          </Field>
        </styles.TextInputContainer>
        {requirePullRequest ? (
          <Tooltip
            content={intl.formatMessage(messages.pullRequestRequiredTooltip)}
          >
            {pullRequestCheckbox}
          </Tooltip>
        ) : (
          pullRequestCheckbox
        )}
        {isCreatePullRequest && (
          <styles.TextInputContainer>
            <Field
              name="src-commit-modal-pr-branch-name-input"
              label={intl.formatMessage(messages.branchNameLabel)}
              isRequired
            >
              {({ fieldProps }: any) => (
                <TextField
                  {...fieldProps}
                  value={pullRequestBranchName}
                  onChange={this.onChangePullRequestBranchName}
                />
              )}
            </Field>
          </styles.TextInputContainer>
        )}
      </Modal>
    );
  }
}

export default injectIntl(CommitModalDialog);
